import {
  Box,
  Paper,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { useTranslation } from 'react-i18next';
import { VectorLogo } from 'components/VectorLogo';
import { truncateString, removeNewLines } from 'shared/utils';

const PREVIEW_MESSAGE_TRUNCATE_LENGTH = 160;

export type PushNotificationPreviewProps = {
  title: string;
  body: string;
  /** Shows a placeholder in place of the body while it is being fetched. */
  isBodyLoading?: boolean;
  /** Shows a short error message in place of the body if it failed to load. */
  isBodyError?: boolean;
  isCritical?: boolean;
  sx?: SxProps<Theme>;
};

const PreviewBody = (props: {
  isBodyLoading: boolean;
  isBodyError: boolean;
  body: string;
}): JSX.Element | string => {
  const { isBodyLoading, isBodyError, body } = props;
  const { t } = useTranslation();

  if (isBodyLoading) {
    return (
      <Skeleton
        variant="text"
        width="80%"
        role="status"
        aria-label={t('common.loading')}
      />
    );
  }

  if (isBodyError) {
    return (
      <Typography component="span" variant="body2" color="error">
        {t('addIncidentReport.loadPreviewError')}
      </Typography>
    );
  }

  return truncateString(removeNewLines(body), PREVIEW_MESSAGE_TRUNCATE_LENGTH);
};

export const PushNotificationPreview = (
  props: PushNotificationPreviewProps,
): JSX.Element => {
  const {
    title,
    body,
    isBodyLoading = false,
    isBodyError = false,
    isCritical = false,
    sx = {},
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const logoColor = theme.palette.deepRed[900];

  const previewPaperSx = {
    padding: 1.5,
    borderRadius: 2,
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  };

  const iconContainerSize = 40;

  return (
    <Paper
      elevation={6}
      sx={[previewPaperSx, ...(Array.isArray(sx) ? sx : [sx])]}
      data-testid="push-notification-preview"
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Paper
          elevation={0}
          sx={{
            width: iconContainerSize,
            height: iconContainerSize,
            minWidth: iconContainerSize,
            minHeight: iconContainerSize,
            flexShrink: 0,
            aspectRatio: '1 / 1',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 2,
            backgroundColor: theme.palette.brand[500],
          }}
        >
          <VectorLogo
            size={28}
            color={logoColor}
            sx={{ display: 'flex', lineHeight: 0 }}
          />
        </Paper>
        <Box sx={{ overflow: 'hidden', width: '100%' }}>
          {isCritical ? (
            <Typography
              component="div"
              variant="subtitle1"
              color="error"
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontWeight: 'bold',
                gap: 0.5,
                marginBottom: 0.5,
              }}
            >
              <WarningRoundedIcon fontSize="inherit" />
              {t('addIncidentReport.criticalNotificationWarning')}
            </Typography>
          ) : null}
          <Stack
            flexDirection="row"
            alignItems="center"
            gap={1}
            justifyContent="space-between"
          >
            <Typography
              variant="body2"
              sx={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                fontWeight: 'bold',
                fontSize: 14,
              }}
            >
              {title}
            </Typography>

            {!isCritical && (
              <Typography variant="subtitle1" color="textSecondary">
                {t('addIncidentReport.previewNow')}
              </Typography>
            )}
          </Stack>

          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            <PreviewBody
              isBodyLoading={isBodyLoading}
              isBodyError={isBodyError}
              body={body}
            />
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};
